import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, ROLE } from '../lib/auth';
import {
  COLOUR_PRESETS, MATRIX_DEFAULTS, PHARMA_DEFAULTS, PHARMA_MAX, PHARMA_MIN, cmykToHex, datamatrixSvg, downloadPng, downloadText,
  gs1ElementString, monthText, normaliseGtin, pharmacodeFileName, pharmacodeSvg, uniformPharmacode, verifyPharmacode, type Gs1Fields, type MatrixOptions, type PharmaOptions, type Rotation,
} from '../lib/barcodes';

/**
 * Barcodes: the SimplyPharma2 tool, in the CRM.  Make a Pharmacode or a GS1
 * DataMatrix for a product, see it at its real size, download it as vector
 * SVG for CorelDraw / Illustrator or as a 1200 dpi PNG, and keep it in the
 * register - one Pharmacode number per product, the next free one offered.
 */

type Kind = 'PHARMACODE' | 'GS1_DATAMATRIX';
interface Entry {
  id: number; kind: Kind; code: string; productId: string; productName: string; clientName: string;
  sapNo: string; artworkNo: string; remarks: string; settings: Record<string, unknown> | null;
  createdOn: string; createdBy: string;
}
interface Product { id: string; name: string; client: string | null }

const field = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
const lbl = 'block text-xs font-semibold text-slate-600 mb-1';
const KIND_TEXT: Record<Kind, string> = { PHARMACODE: 'Pharmacode', GS1_DATAMATRIX: 'GS1 DataMatrix' };

function Num({ label, value, onChange, step = 0.05, min = 0, unit = 'mm' }: { label: string; value: number; onChange: (v: number) => void; step?: number; min?: number; unit?: string }) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      <div className="flex items-center gap-1">
        <input type="number" step={step} min={min} value={Number.isFinite(value) ? value : ''} onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(Number(e.target.value))} className={field} />
        <span className="text-xs text-slate-500">{unit}</span>
      </div>
    </div>
  );
}

function Card({ title, children, right }: { title: string; children: ReactNode; right?: ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-800">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

/** Colour: presets, a picker, #hex, and CMYK that turns into the screen colour. */
function ColourPick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [cmyk, setCmyk] = useState({ c: 0, m: 0, y: 0, k: 100 });
  const setC = (k: keyof typeof cmyk, v: number) => { const n = { ...cmyk, [k]: v }; setCmyk(n); onChange(cmykToHex(n.c, n.m, n.y, n.k)); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {COLOUR_PRESETS.map((p) => (
          <button key={p.hex} type="button" title={p.name} onClick={() => onChange(p.hex)}
            className={`h-7 w-7 rounded-full border-2 ${value.toUpperCase() === p.hex ? 'border-brand-600 ring-2 ring-brand-500/30' : 'border-white shadow'}`}
            style={{ background: p.hex }} />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input type="color" value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-12 rounded border border-slate-300" />
        <input value={value} onChange={(e) => onChange(e.target.value)} className={`${field} w-28 font-mono`} />
        <span className="text-[11px] text-slate-500">{COLOUR_PRESETS.find((p) => p.hex === value.toUpperCase())?.name ?? ''}</span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {(['c', 'm', 'y', 'k'] as const).map((k) => (
          <label key={k} className="text-[11px] font-semibold uppercase text-slate-500">{k}
            <input type="number" min={0} max={100} value={cmyk[k]} onChange={(e) => setC(k, Number(e.target.value))}
              className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </label>
        ))}
      </div>
    </div>
  );
}

function Chips<T extends string | number>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([v, t]) => (
        <button key={String(v)} type="button" onClick={() => onChange(v)}
          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${value === v ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400'}`}>
          {t}
        </button>
      ))}
    </div>
  );
}

export function BarcodesPage() {
  const [tab, setTab] = useState<'make' | 'register'>('make');
  const [editing, setEditing] = useState<Entry | null>(null);
  const reg = useQuery({ queryKey: ['barcodes', ''], queryFn: () => api<{ ready: boolean; rows: Entry[] }>('/api/barcodes') });

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 pt-4 shrink-0">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Barcodes</h1>
        <p className="text-xs text-slate-500">Pharmacode and GS1 DataMatrix for carton artwork — exact size in mm, vector for CorelDraw</p>
        <div className="mt-3 flex gap-6">
          {([['make', 'Make a code'], ['register', 'Register']] as const).map(([k, t]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-1 pb-3 pt-1 text-sm font-semibold ${tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {t}
              {k === 'register' && (reg.data?.rows.length ?? 0) > 0 && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{reg.data!.rows.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6">
        {reg.data && !reg.data.ready && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Codes can be made and downloaded; saving them to the register needs the barcode database update.
          </div>
        )}
        {tab === 'make'
          ? <Maker key={editing?.id ?? 'new'} editing={editing} onDone={() => { setEditing(null); setTab('register'); }} onCancelEdit={() => setEditing(null)} />
          : <Register rows={reg.data?.rows ?? []} loading={reg.isLoading} onOpen={(e) => { setEditing(e); setTab('make'); }} />}
      </div>
    </div>
  );
}

function Maker({ editing, onDone, onCancelEdit }: { editing: Entry | null; onDone: () => void; onCancelEdit: () => void }) {
  const qc = useQueryClient();
  const s = (editing?.settings ?? {}) as Record<string, unknown>;
  const [kind, setKind] = useState<Kind>(editing?.kind ?? 'PHARMACODE');
  // Product and register fields
  const [product, setProduct] = useState<{ id: string; name: string }>({ id: editing?.productId ?? '', name: editing?.productName ?? '' });
  const [term, setTerm] = useState(editing?.productName ?? '');
  const [openList, setOpenList] = useState(false);
  const [sapNo, setSapNo] = useState(editing?.sapNo ?? '');
  const [artworkNo, setArtworkNo] = useState(editing?.artworkNo ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');
  // Pharmacode
  const [pharma, setPharma] = useState<number>(editing?.kind === 'PHARMACODE' ? Number(editing.code) : NaN);
  const [po, setPo] = useState<PharmaOptions>({ ...PHARMA_DEFAULTS, ...((s.pharma as Partial<PharmaOptions>) ?? {}) });
  // DataMatrix
  const [g, setG] = useState<Gs1Fields>({ gtin: '', batch: '', expiry: '', mfg: '', serial: '', ...((s.gs1 as Partial<Gs1Fields>) ?? {}) });
  const [mo, setMo] = useState<MatrixOptions>({ ...MATRIX_DEFAULTS, ...((s.matrix as Partial<MatrixOptions>) ?? {}) });
  const [showLines, setShowLines] = useState<Record<string, boolean>>((s.lines as Record<string, boolean>) ?? { gtin: true, batch: true, mfg: true, expiry: true, serial: true });
  const [msg, setMsg] = useState('');

  const products = useQuery({
    enabled: openList && term.trim().length >= 2,
    queryKey: ['barcode-product', term.trim()],
    queryFn: () => api<Product[]>(`/api/products/autocomplete?q=${encodeURIComponent(term.trim())}`),
    staleTime: 30_000,
  });
  const pick = async (p: Product) => {
    setProduct({ id: p.id, name: p.name }); setTerm(p.name); setOpenList(false);
    try {
      const full = await api<{ artwork?: string; artWNo?: string }>(`/api/products/${encodeURIComponent(p.id)}`);
      if (!artworkNo && full.artwork) setArtworkNo(full.artwork);
      if (!sapNo && full.artWNo) setSapNo(full.artWNo);
    } catch { /* the name is enough */ }
  };

  // Pharmacode: next free number, and whether this one is taken.
  const nextQ = useQuery({ enabled: kind === 'PHARMACODE' && !editing, queryKey: ['barcode-next'], queryFn: () => api<{ next: number | null }>('/api/barcodes/pharmacode/next') });
  useEffect(() => { if (!editing && Number.isNaN(pharma) && nextQ.data?.next) setPharma(nextQ.data.next); }, [nextQ.data, editing, pharma]);
  const validPharma = Number.isInteger(pharma) && pharma >= PHARMA_MIN && pharma <= PHARMA_MAX;
  const takenQ = useQuery({
    enabled: kind === 'PHARMACODE' && validPharma,
    queryKey: ['barcode-taken', pharma],
    queryFn: () => api<{ taken: Entry | null }>(`/api/barcodes/pharmacode/${pharma}`),
  });
  const takenBy = takenQ.data?.taken && takenQ.data.taken.id !== editing?.id ? takenQ.data.taken : null;

  // The drawing
  const gs1 = useMemo(() => gs1ElementString(g), [g]);
  const drawn = useMemo(() => {
    try {
      if (kind === 'PHARMACODE') {
        if (!validPharma) return { error: `A Pharmacode is a whole number from ${PHARMA_MIN} to ${PHARMA_MAX.toLocaleString('en-IN')}` };
        const d = pharmacodeSvg(pharma, po);
        return d ? { ...d, info: `${d.bars.length} bars, left to right (N thin, W thick): ${d.bars.join(' ')}`, check: verifyPharmacode(pharma, d.svg) } : { error: 'Cannot draw this number' };
      }
      if (gs1.error) return { error: gs1.error };
      const gt = normaliseGtin(g.gtin).gtin;
      const lines = [
        showLines.gtin && `GTIN: ${gt}`, showLines.batch && `BATCH: ${g.batch.trim()}`,
        showLines.mfg && g.mfg && `MFG: ${monthText(g.mfg)}`, showLines.expiry && `EXP: ${monthText(g.expiry)}`,
        showLines.serial && g.serial.trim() && `SR NO: ${g.serial.trim()}`,
      ].filter(Boolean) as string[];
      const d = datamatrixSvg(gs1.text, { ...mo, lines });
      return { ...d, info: `${d.size} squares · ${gs1.text}` };
    } catch (e) {
      return { error: String((e as Error).message ?? e).replace(/^bwipp\.\w+#\d+:\s*/, '') };
    }
  }, [kind, pharma, po, validPharma, gs1, g, mo, showLines]);
  const ok = 'svg' in drawn ? drawn : null;

  const codeValue = kind === 'PHARMACODE' ? String(pharma) : gs1.text;
  const fileBase = kind === 'PHARMACODE' ? pharmacodeFileName(product.name || term, sapNo, pharma) : `datamatrix-${normaliseGtin(g.gtin).gtin}-${g.batch.trim()}`;
  const save = useMutation({
    mutationFn: () => api(editing ? `/api/barcodes/${editing.id}` : '/api/barcodes', {
      method: editing ? 'PUT' : 'POST',
      body: JSON.stringify({
        kind, code: codeValue, productId: product.id || null, productName: product.name || term.trim() || null,
        sapNo: sapNo || null, artworkNo: artworkNo || null, remarks: remarks || null,
        settings: kind === 'PHARMACODE' ? { pharma: po } : { gs1: g, matrix: { ...mo, lines: [] }, lines: showLines },
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['barcodes'] }); qc.invalidateQueries({ queryKey: ['barcode-next'] }); qc.invalidateQueries({ queryKey: ['barcode-taken'] });
      setMsg(editing ? 'Saved.' : `Saved to the register: ${KIND_TEXT[kind]} ${kind === 'PHARMACODE' ? pharma : ''}`);
      if (editing) onDone();
      else if (kind === 'PHARMACODE') setPharma(NaN);
    },
  });
  const saveBlocked = !ok ? 'Fix the code first' : kind === 'PHARMACODE' && uniformPharmacode(pharma) ? 'All bars one width — pick another number' : kind === 'PHARMACODE' && takenBy ? `${pharma} is already ${takenBy.productName || 'used'}'s` : !(product.name || term.trim()) ? 'Pick the product' : null;
  const err = (save.error as Error | null)?.message;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="space-y-4">
        {editing && (
          <div className="flex items-center justify-between rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800">
            <span>Changing <b>{KIND_TEXT[editing.kind]} {editing.kind === 'PHARMACODE' ? editing.code : ''}</b> · {editing.productName}</span>
            <button className="text-xs font-semibold hover:underline" onClick={onCancelEdit}>Make a new one instead</button>
          </div>
        )}
        <Card title="Code">
          <Chips<Kind> value={kind} onChange={(k) => { if (!editing) setKind(k); }} options={[['PHARMACODE', 'Pharmacode'], ['GS1_DATAMATRIX', 'GS1 DataMatrix']]} />
        </Card>

        <Card title="Product">
          <div className="relative">
            <label className={lbl}>Product (from Product Master)</label>
            <input value={term} onChange={(e) => { setTerm(e.target.value); setProduct({ id: '', name: '' }); setOpenList(true); }}
              onFocus={() => setOpenList(true)} onBlur={() => setTimeout(() => setOpenList(false), 150)} placeholder="Type the product name" className={field} />
            {product.id && <div className="mt-1 text-[11px] text-emerald-700">✓ {product.id}</div>}
            {openList && (products.data ?? []).length > 0 && (
              <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                {products.data!.map((p) => (
                  <li key={p.id}><button type="button" onMouseDown={() => void pick(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-brand-50">{p.name} <span className="text-xs text-slate-400">{p.id}</span></button></li>
                ))}
              </ul>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><label className={lbl}>SAP / material no.</label><input value={sapNo} onChange={(e) => setSapNo(e.target.value)} className={field} /></div>
            <div><label className={lbl}>Artwork no.</label><input value={artworkNo} onChange={(e) => setArtworkNo(e.target.value)} className={field} /></div>
          </div>
          <div className="mt-3"><label className={lbl}>Remarks</label><input value={remarks} onChange={(e) => setRemarks(e.target.value)} className={field} /></div>
        </Card>

        {kind === 'PHARMACODE' ? (
          <>
            <Card title="Pharmacode number" right={!editing && nextQ.data?.next ? (
              <button type="button" onClick={() => setPharma(nextQ.data!.next!)} className="text-xs font-semibold text-brand-700 hover:underline">Next free: {nextQ.data.next}</button>
            ) : null}>
              <input type="number" min={PHARMA_MIN} max={PHARMA_MAX} step={1} value={Number.isNaN(pharma) ? '' : pharma}
                onChange={(e) => setPharma(e.target.value === '' ? NaN : Number(e.target.value))} className={`${field} text-lg font-bold`} />
              <div className="mt-1.5 text-xs">
                {!validPharma ? <span className="text-red-600">{PHARMA_MIN} to {PHARMA_MAX.toLocaleString('en-IN')}</span>
                  : uniformPharmacode(pharma) ? <span className="font-semibold text-amber-700">All bars are the same width — a reader cannot tell which way round it is. Pick another number.</span>
                  : takenBy ? <span className="font-semibold text-red-600">Taken: {takenBy.productName || '—'}{takenBy.sapNo ? ` · SAP ${takenBy.sapNo}` : ''} ({takenBy.createdBy})</span>
                  : takenQ.isLoading ? <span className="text-slate-400">Checking…</span>
                  : <span className="font-semibold text-emerald-700">✓ Free</span>}
              </div>
            </Card>
            <Card title="Size" right={
              <div className="flex gap-1.5">
                <button type="button" title="0.6 / 1.8 / 1.2 mm, 16 mm high - as SimplyPharma2 drew it"
                  onClick={() => setPo({ ...po, narrowMm: PHARMA_DEFAULTS.narrowMm, wideMm: PHARMA_DEFAULTS.wideMm, gapMm: PHARMA_DEFAULTS.gapMm, heightMm: PHARMA_DEFAULTS.heightMm })}
                  className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-brand-400">Our standard</button>
                <button type="button" title="Laetus's own sizes: 0.75 / 2.25 / 1.0 mm"
                  onClick={() => setPo({ ...po, narrowMm: 0.75, wideMm: 2.25, gapMm: 1 })}
                  className="rounded border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-brand-400">Laetus</button>
              </div>
            }>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Num label="Bar height" value={po.heightMm} onChange={(v) => setPo({ ...po, heightMm: v })} step={0.5} />
                <Num label="Thin bar" value={po.narrowMm} onChange={(v) => setPo({ ...po, narrowMm: v })} />
                <Num label="Thick bar" value={po.wideMm} onChange={(v) => setPo({ ...po, wideMm: v })} />
                <Num label="Gap" value={po.gapMm} onChange={(v) => setPo({ ...po, gapMm: v })} />
                <Num label="Clear space each side" value={po.quietMm} onChange={(v) => setPo({ ...po, quietMm: v })} step={0.5} />
              </div>
              {(po.narrowMm < 0.5 || po.wideMm < po.narrowMm * 2.5 || po.gapMm < 0.5) && (
                <div className="mt-2 text-xs text-amber-700">Laetus readers want a thin bar of 0.5 mm or more, a thick bar about 3 × the thin one, and gaps of 0.5 mm or more.</div>
              )}
            </Card>
          </>
        ) : (
          <>
            <Card title="GS1 data">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className={lbl}>GTIN (13 digits get their check digit)</label>
                  <input value={g.gtin} onChange={(e) => setG({ ...g, gtin: e.target.value })} className={`${field} font-mono`} placeholder="0890123456789" />
                  {g.gtin && <div className={`mt-1 text-[11px] ${normaliseGtin(g.gtin).error ? 'text-red-600' : 'text-emerald-700'}`}>{normaliseGtin(g.gtin).error ?? `✓ ${normaliseGtin(g.gtin).gtin}`}</div>}
                </div>
                <div><label className={lbl}>Batch (10)</label><input value={g.batch} onChange={(e) => setG({ ...g, batch: e.target.value.toUpperCase() })} className={field} /></div>
                <div><label className={lbl}>Serial (21) — optional</label><input value={g.serial} onChange={(e) => setG({ ...g, serial: e.target.value })} className={field} /></div>
                <div><label className={lbl}>Mfg month (11) — optional</label><input type="month" value={g.mfg} onChange={(e) => setG({ ...g, mfg: e.target.value })} className={field} /></div>
                <div><label className={lbl}>Expiry month (17)</label><input type="month" value={g.expiry} onChange={(e) => setG({ ...g, expiry: e.target.value })} className={field} /></div>
              </div>
              <div className="mt-3">
                <div className={lbl}>Text beside the code</div>
                <div className="flex flex-wrap gap-3 text-sm">
                  {([['gtin', 'GTIN'], ['batch', 'Batch'], ['mfg', 'Mfg'], ['expiry', 'Exp'], ['serial', 'Sr no']] as const).map(([k, t]) => (
                    <label key={k} className="flex items-center gap-1.5"><input type="checkbox" checked={!!showLines[k]} onChange={(e) => setShowLines({ ...showLines, [k]: e.target.checked })} />{t}</label>
                  ))}
                </div>
              </div>
            </Card>
            <Card title="Size">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Num label="Square (module)" value={mo.moduleMm} onChange={(v) => setMo({ ...mo, moduleMm: v })} step={0.01} />
                <Num label="Clear space" value={mo.quietModules} onChange={(v) => setMo({ ...mo, quietModules: Math.max(0, Math.round(v)) })} step={1} unit="squares" />
              </div>
              {mo.moduleMm < 0.25 && <div className="mt-2 text-xs text-amber-700">Squares under 0.25 mm may not scan on carton board.</div>}
            </Card>
          </>
        )}

        <Card title="Look">
          <div className="space-y-3">
            <div><div className={lbl}>Colour</div>
              <ColourPick value={kind === 'PHARMACODE' ? po.color : mo.color} onChange={(c) => (kind === 'PHARMACODE' ? setPo({ ...po, color: c }) : setMo({ ...mo, color: c }))} /></div>
            <div><div className={lbl}>Background</div>
              <Chips value={(kind === 'PHARMACODE' ? po.background : mo.background) === 'none' ? 'none' : 'white'} options={[['none', 'Transparent'], ['white', 'White']]}
                onChange={(v) => { const b = v === 'none' ? 'none' : '#FFFFFF'; if (kind === 'PHARMACODE') setPo({ ...po, background: b }); else setMo({ ...mo, background: b }); }} /></div>
            {kind === 'PHARMACODE' && (
              <div><div className={lbl}>Number as text</div>
                <Chips value={po.text} options={[['below', 'Below'], ['above', 'Above'], ['none', 'None']]} onChange={(v) => setPo({ ...po, text: v })} /></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Num label="Text size" value={kind === 'PHARMACODE' ? po.fontPt : mo.fontPt} unit="pt" step={0.5}
                onChange={(v) => (kind === 'PHARMACODE' ? setPo({ ...po, fontPt: v }) : setMo({ ...mo, fontPt: v }))} />
              <div><div className={lbl}>Turn</div>
                <Chips<Rotation> value={kind === 'PHARMACODE' ? po.rotate : mo.rotate} options={[[0, '0°'], [90, '90°'], [180, '180°'], [270, '270°']]}
                  onChange={(v) => (kind === 'PHARMACODE' ? setPo({ ...po, rotate: v }) : setMo({ ...mo, rotate: v }))} /></div>
            </div>
          </div>
        </Card>
      </div>

      {/* The code, real size and enlarged */}
      <div className="space-y-4 xl:sticky xl:top-0 xl:self-start">
        <Card title="Preview" right={ok ? <span className="text-xs font-semibold text-slate-500">{ok.widthMm} × {ok.heightMm} mm</span> : null}>
          {'error' in drawn ? (
            <div className="rounded-lg border border-dashed border-red-200 bg-red-50 px-4 py-10 text-center text-sm text-red-700">{drawn.error}</div>
          ) : (
            <>
              <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-slate-200 p-6" style={{ background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 50% / 16px 16px' }}>
                <img alt="barcode" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(ok!.svg)}`}
                  style={{ width: `${Math.min(ok!.widthMm * 6, 520)}px`, maxWidth: '100%' }} />
              </div>
              <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                <span>Real size:</span>
                <img alt="" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(ok!.svg)}`} style={{ width: `${ok!.widthMm}mm`, height: `${ok!.heightMm}mm` }} />
              </div>
              <div className="mt-2 break-all font-mono text-[11px] text-slate-500">{ok!.info}</div>
              {/* The drawing read back as a scanner would, and checked against
                  a second, independent encoder (BWIPP). */}
              {'check' in ok! && ok!.check && (
                <div className={`mt-2 rounded-md border px-3 py-2 text-xs font-medium ${ok!.check.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-700'}`}>
                  {ok!.check.ok ? '✓ Verified: ' : '✗ Check failed: '}{ok!.check.message}
                </div>
              )}
            </>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button disabled={!ok} onClick={() => ok && downloadText(`${fileBase}.svg`, ok.svg)}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-40">Download SVG (vector)</button>
            <button disabled={!ok} onClick={() => ok && void downloadPng(`${fileBase}.png`, ok.svg, ok.widthMm, ok.heightMm, 1200).catch((e) => setMsg((e as Error).message))}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Download PNG (1200 dpi)</button>
          </div>
        </Card>

        <Card title={editing ? 'Save changes' : 'Keep it in the register'}>
          <div className="flex flex-wrap items-center gap-3">
            <button disabled={!!saveBlocked || save.isPending} onClick={() => { setMsg(''); save.mutate(); }}
              className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50">
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Save to register'}
            </button>
            <span className="text-xs text-slate-500">{saveBlocked ?? (kind === 'PHARMACODE' ? `Pharmacode ${pharma} for ${product.name || term}` : '')}</span>
          </div>
          {msg && <div className="mt-2 text-sm font-medium text-emerald-700">✓ {msg}</div>}
          {err && <div className="mt-2 text-sm text-red-600">{err === 'pharmacode_taken' ? 'That Pharmacode was just taken — pick another.' : err === 'migration_needed' ? 'The barcode database update has not been run yet.' : `Could not save: ${err}`}</div>}
        </Card>
      </div>
    </div>
  );
}

function Register({ rows, loading, onOpen }: { rows: Entry[]; loading: boolean; onOpen: (e: Entry) => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | Kind>('');
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = rows.filter((r) => (!kind || r.kind === kind)
    && words.every((w) => `${r.code} ${r.productName} ${r.sapNo} ${r.artworkNo} ${r.clientName}`.toLowerCase().includes(w)));
  const del = useMutation({
    mutationFn: (id: number) => api(`/api/barcodes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['barcodes'] }); qc.invalidateQueries({ queryKey: ['barcode-next'] }); },
  });
  const download = (r: Entry) => {
    const st = (r.settings ?? {}) as Record<string, unknown>;
    if (r.kind === 'PHARMACODE') {
      const d = pharmacodeSvg(Number(r.code), { ...PHARMA_DEFAULTS, ...((st.pharma as Partial<PharmaOptions>) ?? {}) });
      if (d) downloadText(`${pharmacodeFileName(r.productName, r.sapNo, r.code)}.svg`, d.svg);
    } else {
      onOpen(r);
    }
  };
  const exportCsv = () => {
    const lines = [['Code', 'Type', 'Product', 'Client', 'SAP / material no', 'Artwork no', 'Remarks', 'Made by', 'Made on'],
      ...shown.map((r) => [r.code, KIND_TEXT[r.kind], r.productName, r.clientName, r.sapNo, r.artworkNo, r.remarks, r.createdBy, r.createdOn.slice(0, 10)])];
    downloadText('barcode-register.csv', '﻿' + lines.map((l) => l.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n'), 'text/csv;charset=utf-8');
  };
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, product, SAP no, artwork, client" className={`${field} md:w-96`} />
        <Chips<'' | Kind> value={kind} options={[['', 'All'], ['PHARMACODE', 'Pharmacode'], ['GS1_DATAMATRIX', 'DataMatrix']]} onChange={setKind} />
        <button onClick={exportCsv} disabled={!shown.length} className="ml-auto rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Download for Excel</button>
      </div>
      {loading ? <div className="text-slate-500">Loading…</div> : !shown.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-slate-500">{rows.length ? 'Nothing matches.' : 'No codes in the register yet.'}</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">SAP no</th><th className="px-4 py-3">Artwork</th><th className="px-4 py-3">Made</th><th className="px-4 py-3" /></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shown.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono font-bold text-slate-900 break-all max-w-[260px]">{r.code}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{KIND_TEXT[r.kind]}</td>
                  <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{r.productName}</div><div className="text-xs text-slate-500">{r.clientName}</div></td>
                  <td className="px-4 py-2.5 text-slate-700">{r.sapNo || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.artworkNo || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{r.createdBy} · {new Date(`${r.createdOn}Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => onOpen(r)} className="mr-3 text-xs font-semibold text-brand-700 hover:underline">Open</button>
                    {r.kind === 'PHARMACODE' && <button onClick={() => download(r)} className="mr-3 text-xs font-semibold text-slate-600 hover:underline">SVG</button>}
                    {user?.rollId === ROLE.SuperAdmin && (
                      <button onClick={() => { if (confirm(`Remove ${KIND_TEXT[r.kind]} ${r.kind === 'PHARMACODE' ? r.code : ''} (${r.productName}) from the register?${r.kind === 'PHARMACODE' ? '\n\nIts number becomes free again.' : ''}`)) del.mutate(r.id); }}
                        className="text-xs font-semibold text-red-600 hover:underline">Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
